<template>
  <v-form ref="formRef" class="free-consumer-form method-form">
    <!-- Erzeuger-Verknüpfung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-link-variant</v-icon>
        <span>Erzeuger-Verknüpfung</span>
      </div>

      <!-- Generator Type -->
      <v-autocomplete
        v-model="form.generatorType"
        :items="generatorTypes"
        :error-messages="validationErrors?.generator_type"
        item-title="name"
        item-value="name"
        label="Wärmequelle"
        clearable
        :loading="!actionList?.length"
        validate-on-blur
        class="mb-3"
        @update:model-value="deleteGeneratorName"
      >
        <template #append>
          <v-tooltip v-if="hint.generator_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.generator_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.generator_type"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Wärmequelle übernehmen"
            @click="copyFromReference('generator_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Generator Name -->
      <v-autocomplete
        v-if="form.generatorType"
        v-model="form.actionId"
        :items="filteredGeneratorNames"
        item-title="name"
        item-value="id"
        :error-messages="validationErrors?.action_id"
        label="Wärmeerzeuger"
        validate-on-blur
        class="mb-3 ml-8"
        @update:model-value="filterProducer"
      >
        <template #append>
          <v-tooltip v-if="hint.action_id" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.action_id }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Heizart without linked generator -->
      <v-autocomplete
        v-if="!form.generatorType && !form.actionId"
        v-model="form.heatType"
        :items="heatTypes"
        item-title="name"
        item-value="id"
        :error-messages="validationErrors?.heat_type"
        label="Heizart"
        :hint="hint.heat_type"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.heat_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.heat_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.heat_type"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Heizart übernehmen"
            @click="copyFromReference('heat_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Read-only heating type from linked generator -->
      <v-text-field
        v-if="form.actionId"
        v-model="fuelName"
        label="Heizart"
        type="text"
        readonly
        :hint="hint.fuel_name"
        :error-messages="validationErrors?.fuel_name"
        validate-on-blur
        class="mb-3 ml-8"
      />

      <!-- COP -->
      <v-text-field
        v-if="form.heatType === 1"
        v-model.number="form.cop"
        type="number"
        :error-messages="validationErrors?.cop"
        label="COP"
        :hint="hint.cop"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.cop" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.cop }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.cop"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für COP übernehmen"
            @click="copyFromReference('cop')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-counter</v-icon>
        <span>Basisdaten</span>
      </div>

      <!-- Anzahl -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Anzahl übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Energieverbrauch -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-lightning-bolt-outline</v-icon>
        <span>Energieverbrauch</span>
      </div>

      <!-- Electricity -->
      <v-text-field
        v-model.number="form.electricity"
        type="number"
        :error-messages="validationErrors?.electricity"
        label="Stromverbrauch"
        suffix="kWh/a"
        :hint="hint.electricity"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.electricity" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.electricity }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.electricity"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Stromverbrauch übernehmen"
            @click="copyFromReference('electricity')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Heat -->
      <v-text-field
        v-model.number="form.heat"
        type="number"
        :error-messages="validationErrors?.heat"
        label="Wärmeverbrauch"
        suffix="kWh/a"
        :hint="hint.heat"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.heat" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.heat }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.heat"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Wärmeverbrauch übernehmen"
            @click="copyFromReference('heat')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Cold -->
      <v-text-field
        v-model.number="form.cold"
        type="number"
        :error-messages="validationErrors?.cold"
        label="Kälteverbrauch"
        suffix="kWh/a"
        :hint="hint.cold"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.cold" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.cold }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.cold"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Kälteverbrauch übernehmen"
            @click="copyFromReference('cold')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Kühlung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-snowflake</v-icon>
        <span>Kühlung</span>
      </div>

      <!-- Cooling register -->
      <v-checkbox
        v-model="form.coolingRegister"
        :error-messages="validationErrors?.cooling_register"
        label="Kühlregister"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.cooling_register" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.cooling_register }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.cooling_register"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Kühlregister übernehmen"
            @click="copyFromReference('cooling_register')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- EER -->
      <v-text-field
        v-if="form.coolingRegister"
        v-model.number="form.eer"
        type="number"
        :error-messages="validationErrors?.eer"
        label="EER"
        :hint="hint.eer"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.eer" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.eer }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.eer"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für EER übernehmen"
            @click="copyFromReference('eer')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import { useProjectStore } from '@/stores/projectStore'
import { storeToRefs } from 'pinia'
import api from '@/plugins/axios'


/* PROPS */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])


/* Pinia */
const projectStore = useProjectStore()
const { currentProjectActions: actionList } = storeToRefs(projectStore)


/* Field mapping */
const fields = {
  generatorType: 'generator_type',
  actionId:      'action_id',
  amount:        'amount',
  electricity:   'electricity',
  heat:          'heat',
  cold:          'cold',
  coolingRegister: 'cooling_register',
  eer:           'eer',
  heatType:      'heat_type',
  cop:           'cop',
}


/* BaseForm */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
  generatorTypes,
  filteredGeneratorNames,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {},
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => false),
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (e, p) => emit(e, p)
})


/* Local state */
const formRef = ref(null)
const fuelName = ref(null)
const heatTypes = ref([])


/* Expose validator */
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})



/* Watchers */
watch(() => form.coolingRegister, (val, old) => {
  if (old !== null && val === false) form.eer = null
})

watch(() => form.heatType, () => {
  fetchHeatTypes()
}, { immediate: true })

watch(() => form.heatType, (val) => {
  if (Number(val) !== 1) form.cop = null
}, { immediate: true })

watch(() => form.generatorType, (newVal, oldVal) => {
  if (oldVal !== null && newVal === null) deleteFlow()
})


/* Lifecycle */
onMounted(() => {
  fetchHeatTypes()
  fetchHints()
})


/* Methods */
function deleteGeneratorName() {
  if (!form.generatorType) {
    form.actionId = null
    form.heatType = null
    fuelName.value = null
  }
}


function deleteFlow() {
  form.generatorType = null
  form.actionId = null
  form.heatType = null
  fuelName.value = null
}

async function filterProducer() {
  if (!form.actionId) {
    fuelName.value = null
    form.heatType = null
    return
  }

  const action = actionList.value?.find(
    actionItem => actionItem.id === form.actionId
  )

  if (!action) {
    fuelName.value = null
    form.heatType = null
    return
  }

  const actionData = props.reference
    ? action.reference
    : action.planned

  const fuelId = actionData?.fuel

  if (!fuelId) {
    fuelName.value = null
    form.heatType = null
    return
  }

  await fetchProducerFuel(fuelId)
}

async function fetchHeatTypes() {
  try {
    const res = await api.get('/methods/hvac/heat-sources/')
    heatTypes.value = res.data
    if (res.data.length === 1) {
      form.heatType = res.data[0].id
    }
  } catch {}
}

async function fetchProducerFuel(fuelId) {
  try {
    const { data: fuel } = await api.get('/methods/hvac/fuel/', {
      params: { id: fuelId },
    })

    fuelName.value = fuel?.name ?? null
    form.heatType = fuel?.id ?? null
  } catch (error) {
    fuelName.value = null
    form.heatType = null
    console.error('Brennstoff konnte nicht geladen werden:', error)
  }
}

</script>

<style scoped lang="scss">
.free-consumer-form {
  width: 100%;
}

/* Input fields */
.free-consumer-form :deep(.v-field) {
  border-radius: 18px;
}

.free-consumer-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Checkbox */
.free-consumer-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested dependent fields */
.free-consumer-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Help and reference-copy actions */
.free-consumer-form :deep(.v-input__append),
.free-consumer-form :deep(.v-field__append-inner) {
  align-items: center;
}

.free-consumer-form :deep(.v-input__append .v-icon),
.free-consumer-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.free-consumer-form :deep(.v-input__append .v-btn),
.free-consumer-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.free-consumer-form :deep(.v-input__append .v-btn:hover),
.free-consumer-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Read-only fields */
.free-consumer-form :deep(.v-field--active.v-field--readonly) {
  background-color: rgba(var(--v-theme-on-surface), 0.025);
}

/* Disabled states */
.free-consumer-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .free-consumer-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
``